import { Helmet } from "react-helmet-async";
import { styled, useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  Container,
  Link,
  Grid,
  List,
  ListItem,
} from "@mui/material";

import Logo from "../components/Logo";
import SimpleFooter from "../components/SimpleFooter";

const StyledPageContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
});

const ContentWrapper = styled("div")(({ theme }) => ({
  flexGrow: 1,
  display: "flex",
  flexDirection: "column",
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(8),
}));

const sections = [
  {
    id: "introduction",
    title: "1. Introduction",
    content: (
      <Typography variant="body1" paragraph>
        Welcome to Contactie
        <Box component="sup" sx={{ fontSize: "0.6em", verticalAlign: "super" }}>
          TM
        </Box>{" "}
        Privacy Policy (Beta). We are a Digital Identity and Reputation Platform
        based in <strong>Tunisia</strong>. We are committed to protecting your
        privacy and complying with the regulations set forth by the{" "}
        <strong> Law </strong> regarding the protection of personal data. By
        using the Service, you agree to the collection and use of information in
        accordance with this policy.
      </Typography>
    ),
  },
  {
    id: "definitions",
    title: "2. Definitions",
    content: (
      <Box sx={{ pl: 2 }}>
        <Typography variant="body1" paragraph>
          <strong>Company</strong> – refers to Contactie
          <Box
            component="sup"
            sx={{ fontSize: "0.6em", verticalAlign: "super" }}
          >
            TM
          </Box>
          , also referred to as “We”, “Us”, or “Our”.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Personal Data</strong> – any information that relates to an
          identified or identifiable individual, such as a name, email address,
          or phone number.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Service</strong> – the Contactie
          <Box
            component="sup"
            sx={{ fontSize: "0.6em", verticalAlign: "super" }}
          >
            TM
          </Box>{" "}
          website, platform, and physical NFC products.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Usage Data</strong> – data collected automatically from your
          interaction with the Service (e.g., rating history, AI usage).
        </Typography>
      </Box>
    ),
  },
  {
    id: "collection",
    title: "3. Information We Collect",
    content: (
      <>
        <Typography
          variant="h6"
          component="h3"
          sx={{ fontWeight: 600, mt: 2, mb: 1 }}
        >
          Personal & Professional Data
        </Typography>
        <Typography variant="body1" paragraph>
          When you create an account, we require you to provide certain Personal
          Data. Additionally, to power our AI features, we collect professional
          documents:
        </Typography>
        <List sx={{ listStyleType: "disc", pl: 4 }}>
          <ListItem sx={{ display: "list-item", py: 0.5 }}>
            <strong>Identity:</strong> Username, Full Name, Email Address, Phone
            Number.
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0.5 }}>
            <strong>Professional:</strong> Curricula Vitae (CV/Resumes) and Job
            Descriptions you upload for analysis.
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0.5 }}>
            <strong>Public Profile:</strong> Pictures, Social Links, and
            Biography.
          </ListItem>
        </List>

        <Typography
          variant="h6"
          component="h3"
          sx={{ fontWeight: 600, mt: 3, mb: 1 }}
        >
          Transaction & Delivery Data
        </Typography>
        <Typography variant="body1" paragraph>
          For hardware orders (NFC Cards) during our Beta phase, we utilize a{" "}
          <strong>"Cash on Delivery"</strong> system. We collect:
        </Typography>
        <List sx={{ listStyleType: "disc", pl: 4 }}>
          <ListItem sx={{ display: "list-item", py: 0.5 }}>
            Physical Delivery Address.
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0.5 }}>
            Order History and Payment Status (Paid/Pending).
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0.5 }}>
            <em>
              Note: We do not currently collect credit card information online.
            </em>
          </ListItem>
        </List>

        <Typography
          variant="h6"
          component="h3"
          sx={{ fontWeight: 600, mt: 3, mb: 1 }}
        >
          Usage Data & Cookies
        </Typography>
        <Typography variant="body1" paragraph>
          We use essential cookies to manage your session. We also track basic
          analytics (such as profile views via NFC scans) to provide you with
          insights.
        </Typography>
      </>
    ),
  },
  {
    id: "ai-processing",
    title: "4. Artificial Intelligence (Google Gemini)",
    content: (
      <>
        <Typography variant="body1" paragraph>
          Our Service uses the <strong>Google Gemini API</strong> to provide
          "Talent-to-Role" Fit Analysis.
        </Typography>
        <List sx={{ listStyleType: "disc", pl: 4 }}>
          <ListItem sx={{ display: "list-item", py: 0.5 }}>
            <strong>Processing:</strong> Text from uploaded CVs and Job
            Descriptions is transmitted securely to Google Gemini for analysis.
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0.5 }}>
            <strong>Privacy:</strong> Your data is <strong>not used</strong> to
            train Google's public AI models. It is processed solely to generate
            your compatibility report.
          </ListItem>
        </List>
      </>
    ),
  },
  {
    id: "use-of-data",
    title: "5. How We Use Your Information",
    content: (
      <>
        <Typography variant="body1" paragraph>
          We use the information we collect for the following purposes:
        </Typography>
        <List sx={{ listStyleType: "disc", pl: 4 }}>
          <ListItem sx={{ display: "list-item", py: 0.5 }}>
            To provide the Digital Identity service and generate your Trust
            Score (using our "Recency Algorithm").
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0.5 }}>
            To facilitate the delivery of physical products (NFC Cards) to your
            address.
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0.5 }}>
            To provide AI-powered career insights.
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0.5 }}>
            To contact You with essential service updates.
          </ListItem>
        </List>
      </>
    ),
  },
  {
    id: "sharing",
    title: "6. Sharing and Disclosure of Information",
    content: (
      <>
        <Typography variant="body1" paragraph>
          <strong>
            We do not sell, rent, or share your Personal Data with third parties
            for marketing purposes.
          </strong>
        </Typography>
        <Typography variant="body1" paragraph>
          We may disclose your information in these specific circumstances:
        </Typography>
        <List sx={{ listStyleType: "disc", pl: 4 }}>
          <ListItem sx={{ display: "list-item", py: 0.5 }}>
            <strong>Logistics Partners:</strong> Your Name, Address, and Phone
            Number are shared with delivery services (e.g., Local Couriers)
            solely to fulfill your "Cash on Delivery" orders.
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0.5 }}>
            <strong>Service Providers:</strong> Google (for AI processing) and
            Hosting Providers.
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0.5 }}>
            <strong>Legal Compliance:</strong> If required by Tunisian law or to
            protect the integrity of our Reputation System against fraud.
          </ListItem>
        </List>
      </>
    ),
  },
  {
    id: "transfer",
    title: "7. Transfer of Your Data",
    content: (
      <Typography variant="body1" paragraph>
        Your information is processed and stored on secure servers located in{" "}
        <strong>France</strong> (Europe) to ensure high availability. By using
        the Service, you consent to the transfer of your information outside of
        Tunisia to secure jurisdictions that comply with GDPR standards,
        offering a level of protection compatible with Tunisian law.
      </Typography>
    ),
  },
  {
    id: "integrity",
    title: "8. Rating System Integrity",
    content: (
      <Typography variant="body1" paragraph>
        To maintain the trust and value of the Contactie platform for all
        professionals,{" "}
        <strong>
          Verified Ratings cannot be deleted or hidden by the user
        </strong>{" "}
        once accepted. This ensures that our "Trust Score" remains an honest
        representation of professional history. If a rating violates our terms
        (e.g., hate speech), it can be reported for manual review.
      </Typography>
    ),
  },
  {
    id: "rights",
    title: "9. Your Data Rights (Tunisian Law)",
    content: (
      <>
        <Typography variant="body1" paragraph>
          Under Law, you have the right to:
        </Typography>
        <List sx={{ listStyleType: "disc", pl: 4 }}>
          <ListItem sx={{ display: "list-item", py: 0.5 }}>
            <strong>Access and Update:</strong> You can review and update your
            public profile and private data in your "Settings".
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0.5 }}>
            <strong>Object:</strong> You may object to data processing for
            legitimate reasons, provided it does not conflict with the integrity
            of the Rating System.
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0.5 }}>
            <strong>Account Deletion:</strong> You may request deletion of your
            account by emailing{" "}
            <Link href="mailto:contact@contactie.com">
              contact@contactie.com
            </Link>
            .
          </ListItem>
        </List>
      </>
    ),
  },
  {
    id: "security",
    title: "10. Security of Your Data",
    content: (
      <Typography variant="body1" paragraph>
        The security of your data is a priority. We use SSL/HTTPS encryption for
        all data transmission and hash user passwords. However, please remember
        that no method of transmission over the Internet is 100% secure.
      </Typography>
    ),
  },
  {
    id: "students",
    title: "11. Minors and University Students",
    content: (
      <Typography variant="body1" paragraph>
        Our Service is intended for professionals and{" "}
        <strong>university students</strong>. We do not knowingly collect
        personally identifiable information from anyone under the age of 18. If
        you are a university student under 18, you must have parental consent to
        use the Service. If we become aware that we have collected Personal Data
        from a minor without verification of parental consent, we take steps to
        remove that information.
      </Typography>
    ),
  },
  {
    id: "changes",
    title: "12. Changes to This Privacy Policy",
    content: (
      <Typography variant="body1" paragraph>
        We may update our Privacy Policy from time to time to reflect changes in
        our Beta service. We will notify you of any changes by posting the new
        Privacy Policy on this page and updating the "Last Updated" date.
      </Typography>
    ),
  },
  {
    id: "contact",
    title: "13. Contact Us",
    content: (
      <Typography variant="body1" paragraph>
        If you have any questions about this Privacy Policy, please contact us
        at:{" "}
        <Link href="mailto:contact@contactie.com">contact@contactie.com</Link>
      </Typography>
    ),
  },
];

export default function PrivacyPolicyPage() {
  const theme = useTheme();

  return (
    <>
      <Helmet>
        <title>Privacy Policy | Contactie™</title>
        <meta
          name="description"
          content="Read the official Privacy Policy for the Contactie™ platform. Understand what data we collect and how we use it to provide our service."
        />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <StyledPageContainer>
        <Box
          component="header"
          sx={{ py: 3, px: 5, display: { xs: "none", md: "flex" } }}
        >
          <Logo sx={{ height: 30, width: "auto" }} />
        </Box>

        <ContentWrapper>
          <Container maxWidth="lg">
            <Box sx={{ mb: 5, textAlign: { xs: "center", md: "left" } }}>
              <Box
                sx={{
                  mb: 5,
                  display: { xs: "flex", md: "none" },
                  justifyContent: "center",
                }}
              >
                <Logo sx={{ height: 35, width: "auto" }} />
              </Box>
              <Typography variant="h2" component="h1" sx={{ fontWeight: 700 }}>
                Contactie
                <Box
                  component="sup"
                  sx={{ fontSize: "0.6em", verticalAlign: "super" }}
                >
                  TM
                </Box>{" "}
                Privacy Policy (Beta)
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                Last Updated:{" "}
                {new Date().toLocaleString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </Typography>
            </Box>

            <Grid container spacing={5}>
              <Grid item xs={12} md={8}>
                {sections.map((section) => (
                  <Box key={section.id} id={section.id} sx={{ mb: 5 }}>
                    <Typography
                      variant="h4"
                      component="h2"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      {section.title}
                    </Typography>
                    {section.content}
                  </Box>
                ))}
              </Grid>

              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    position: "sticky",
                    top: theme.spacing(4),
                    display: { xs: "none", md: "block" },
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    On this page
                  </Typography>
                  <List dense>
                    {sections.map((section) => (
                      <ListItem key={section.id} disablePadding>
                        <Link
                          href={`#${section.id}`}
                          underline="hover"
                          sx={{ color: "text.secondary", py: 0.5 }}
                        >
                          {section.title}
                        </Link>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </ContentWrapper>
        <SimpleFooter />
      </StyledPageContainer>
    </>
  );
}
